<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { STATUS_CLOSED, STATUS_OPEN } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import ProgressSpinner from 'primevue/progressspinner';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import UpdateGroupTypeModal from '@/components/modal/UpdateGroupTypeModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const groupTypeManagement = useGroupTypeStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const groupTypeList = ref([]);
const filters = ref();
const loading = ref(false);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const dialog = useDialog();

const getgroupTypeData = () => {
  loading.value = true;
  groupTypeManagement
    .getGroupTypeManagementList()
    .then((response) => {
      groupTypeList.value = response.data;
    })
    .finally(() => (loading.value = false));
};
getgroupTypeData();

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    instaQuote: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    status: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const clearFilter = () => {
  initFilters();
};
const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};
const onUpdateStatus = async (sk: string, status: string) => {
  try {
    await groupTypeManagement.updateStatus(sk, status);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};
const deleteGroupType = (data?: any) => {
  groupTypeManagement
    .deleteGroupType(data.sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      getgroupTypeData();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};
const updateGroupType = (data?: any) => {
  const dialogRef = dialog.open(UpdateGroupTypeModal, {
    props: {
      header: data ? t('management.group_type.update_group_type') : t('management.group_type.add_group_type'),
      modal: true,
      maximizable: true,
      data: cloneDeep(data) || {
        pk: '',
        sk: '',
        name: '',
        nameFr: '',
        addToHotel: false,
        status: STATUS_OPEN,
        createdDate: new Date(),
        updatedDate: new Date()
      }
    },
    emits: {
      closeModal: () => {
        dialogRef.close();
        getgroupTypeData();
      }
    }
  });
};

const onUpdateStatusInstaQuote = async (sk: string, instaQuote) => {
  try {
    await groupTypeManagement.updateStatusInstaQuote(sk, instaQuote);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};
</script>

<template>
  <LoadingSpinner v-if="loading" />
  <div class="card group-type">
    <div class="font-bold mb-3 text-xl uppercase">{{ $t('management.group_type.group_type_management') }}</div>
    <DataTable v-model:filters="filters" :value="groupTypeList" paginator showGridlines :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['name', 'status']">
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left flex-col sm:flex-row gap-3">
            <Button id="group-types-clear-btn" type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search"></i>
              <InputText id="group-types-search" v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="name" :header="$t('management.group_type.group_type_name')" class="break-word" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <span class="long-text" v-tooltip="data.name">
            {{ data.name }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.group_type.search_by_group_type')" />
        </template>
      </TableColumn>
      <TableColumn field="nameFr" :header="$t('management.group_type.group_type_name_fr')" class="break-word" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <span class="long-text" v-tooltip="data.nameFr">
            {{ data.nameFr }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.group_type.search_by_group_type')" />
        </template>
      </TableColumn>
      <TableColumn field="instaQuote" :header="$t('management.air.instance_quote')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <ToggleSwitch :disabled="!authorizations?.configurationPage?.group_type_management_edit" v-model="data.instaQuote" trueValue="OPEN" falseValue="CLOSED" @change="onUpdateStatusInstaQuote(data.sk, data.instaQuote)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn field="status" :header="$t('management.airline.active')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <ToggleSwitch :disabled="!authorizations?.configurationPage?.group_type_management_edit" v-model="data.status" trueValue="OPEN" falseValue="CLOSED" @change="onUpdateStatus(data.sk, data.status)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')" v-if="authorizations?.configurationPage?.group_type_management_edit">
        <template #body="{ data }">
          <div class="flex">
            <Button icon="pi pi-pencil" class="p-button-text" @click="updateGroupType(data)" />
            <Button icon="pi pi-trash" class="p-button-text" @click="deleteGroupType(data)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <div>
      <Button id="group-types-add-btn" v-if="authorizations?.configurationPage?.group_type_management_edit" :label="$t('management.group_type.add_group_type')" icon="pi pi-plus" class="p-button-text" @click="updateGroupType()" />
    </div>
  </div>
</template>
